import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { fmtDateTime } from "../../lib/tz";
import { PageHeader, PillBadge, GhostButton } from "../../components/Primitives";
import AbsenceInsights from "../../components/AbsenceInsights";
import RecentScanPhotos from "../../components/RecentScanPhotos";
import OpsHealthBadge from "../../components/OpsHealthBadge";
import ReportsAtAGlance from "../../components/ReportsAtAGlance";
import KpiGrid from "../../components/KpiGrid";
import SitePerformanceCard from "../../components/SitePerformanceCard";
import DashboardCustomizeModal from "../../components/DashboardCustomizeModal";
import { useTheme } from "../../contexts/ThemeContext";
import { ShieldAlert, Settings, RotateCw } from "lucide-react";
import { resolveKpiState, saveKpiPrefs } from "../../lib/dashboardPrefs";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

const TOOLTIP_CURSOR = { fill: "#FF3B3015" };

// Maps the OpsHealthBadge cadence toggle to a real ISO date window used by
// /metrics/operational and /metrics/site-performance so KPI tiles and the
// Site Performance card follow the same 7D / 30D / 90D filter as the score.
const CADENCE_TO_WINDOW = {
  "7d":  { days: 7,  label: "Last 7 days" },
  "30d": { days: 30, label: "Last 30 days" },
  "90d": { days: 90, label: "Last 90 days" },
};

function computeRange(cadence) {
  const now = new Date();
  const days = CADENCE_TO_WINDOW[cadence]?.days ?? 7;
  const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return { start: start.toISOString(), end: now.toISOString() };
}

/**
 * DashboardAdmin — full-featured Overview for admin/super_admin.
 * Vertical scroll with generous whitespace; KPI tiles are user-configurable
 * (both which tiles show AND their order via drag-reorder).
 *
 * Also drives supervisor / ops-manager / CSO variants via the `role` prop —
 * they share the same layout, minus admin-only tiles (filtered by
 * dashboardPrefs.isKpiAllowedForRole).
 *
 * The OpsHealthBadge 7D/30D/90D switcher is the single source of truth for
 * the metrics window: both `/metrics/operational` (Key Metrics) and
 * `/metrics/site-performance` (Site Performance card) re-fetch when the user
 * flips the cadence.
 */
export default function DashboardAdmin({ role = "admin" }) {
  const [stats, setStats] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [certs, setCerts] = useState(null);
  const initialState = useMemo(() => resolveKpiState(role), [role]);
  const [visible, setVisible] = useState(initialState.visible);
  const [sectionOverrides, setSectionOverrides] = useState(initialState.sectionOverrides || {});
  const [sectionLabels, setSectionLabels] = useState(initialState.sectionLabels || {});
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cadence, setCadence] = useState("7d");
  const { theme } = useTheme() || { theme: "dark" };

  const range = useMemo(() => computeRange(cadence), [cadence]);
  const rangeLabel = CADENCE_TO_WINDOW[cadence]?.label || "Last 7 days";

  const tooltipStyle = useMemo(() => ({
    background: theme === "light" ? "#ffffff" : "#0A0A0A",
    border: `1px solid ${theme === "light" ? "#ececee" : "#27272A"}`,
    color: theme === "light" ? "#18181b" : "#ffffff",
    fontSize: 12,
    boxShadow: theme === "light" ? "0 4px 12px rgba(24,24,27,0.08)" : "none",
  }), [theme]);
  const gridStroke = theme === "light" ? "#ececee" : "#27272A";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/dashboard/stats");
      setStats(r.data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("dashboard/stats failed:", err);
    }
    try {
      const m = await api.get("/metrics/operational", {
        params: { start: range.start, end: range.end },
      });
      setMetrics(m.data);
    } catch {
      setMetrics(null);
    }
    try {
      const c = await api.get("/guards/compliance/certifications");
      setCerts(c.data);
    } catch {
      setCerts(null);
    }
    setLoading(false);
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  const persistPrefs = useCallback(({ visible: nextVisible, sectionLabels: nextLabels }) => {
    setVisible(nextVisible);
    setSectionLabels(nextLabels || {});
    saveKpiPrefs(role, nextVisible, sectionOverrides, nextLabels || {});
  }, [role, sectionOverrides]);

  const reorderTiles = useCallback((nextOrder) => {
    setVisible(nextOrder);
    saveKpiPrefs(role, nextOrder, sectionOverrides, sectionLabels);
  }, [role, sectionOverrides, sectionLabels]);

  const setTileSection = useCallback((kpiId, section) => {
    setSectionOverrides((prev) => {
      const next = { ...prev, [kpiId]: section };
      saveKpiPrefs(role, visible, next, sectionLabels);
      return next;
    });
  }, [role, visible, sectionLabels]);

  const severityTone = (s) =>
    ({ critical: "red", high: "red", medium: "amber", low: "blue" }[s] || "default");

  return (
    <div>
      <PageHeader
        title="Overview"
        subtitle="Tactical snapshot of agency operations. Metrics refresh on navigation and on demand."
        action={
          <div className="flex items-center gap-2">
            <GhostButton
              onClick={load}
              data-testid="dashboard-refresh"
              title="Refresh"
            >
              <RotateCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </GhostButton>
            <GhostButton
              onClick={() => setCustomizeOpen(true)}
              data-testid="dashboard-customize-open"
            >
              <Settings className="size-3.5" />
              Customize
            </GhostButton>
          </div>
        }
      />

      <div className="p-8 space-y-10" data-testid="dashboard-admin-root">
        <OpsHealthBadge cadence={cadence} onCadenceChange={setCadence} />

        <section aria-labelledby="dashboard-kpi-heading" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 id="dashboard-kpi-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
                Key Metrics
              </h2>
              <p className="text-zinc-500 text-sm mt-1">
                Coverage & financials · <span className="text-zinc-300">{rangeLabel}</span> · drag any tile to reorder or drop into another section.
              </p>
            </div>
          </div>
          <KpiGrid
            visible={visible}
            stats={stats}
            metrics={metrics}
            role={role}
            sectionOverrides={sectionOverrides}
            sectionLabels={sectionLabels}
            onReorder={reorderTiles}
            onSectionChange={setTileSection}
          />
        </section>

        {certs && (certs.expired.length > 0 || certs.nearing_expiry.length > 0) && (
          <Link
            to={certs.expired.length > 0 ? "/guards?expired=1" : "/reports"}
            data-testid="cert-compliance-banner"
            className="flex items-center gap-3 border border-[#FF3B30]/40 bg-[#FF3B30]/5 hover:bg-[#FF3B30]/10 px-5 py-4 transition-colors group"
          >
            <ShieldAlert className="size-5 text-[#FF3B30] shrink-0" />
            <div className="flex-1 min-w-0 text-sm">
              <span className="text-[#FF3B30] font-bold uppercase tracking-widest text-xs mr-3">Compliance</span>
              {certs.expired.length > 0 && (
                <span className="text-zinc-200">
                  <span className="text-[#FF3B30] font-bold">{certs.expired.length}</span> expired
                </span>
              )}
              {certs.expired.length > 0 && certs.nearing_expiry.length > 0 && <span className="text-zinc-600 mx-2">·</span>}
              {certs.nearing_expiry.length > 0 && (
                <span className="text-zinc-200">
                  <span className="text-[#FF9F0A] font-bold">{certs.nearing_expiry.length}</span> expiring in ≤ {certs.threshold_days} days
                </span>
              )}
            </div>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-white">View →</span>
          </Link>
        )}

        <section aria-labelledby="dashboard-glance-heading" className="space-y-4">
          <div>
            <h2 id="dashboard-glance-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
              Reports at a Glance
            </h2>
            <p className="text-zinc-500 text-sm mt-1">Four report families over the current window · flip the range at the top of the page.</p>
          </div>
          <ReportsAtAGlance cadence={cadence} />
        </section>

        <section aria-labelledby="dashboard-worst-heading" className="space-y-4">
          <div>
            <h2 id="dashboard-worst-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
              Site Performance
            </h2>
            <p className="text-zinc-500 text-sm mt-1">
              Coverage-quality leaderboard for CSO/OM · <span className="text-zinc-300">{rangeLabel}</span> — ranking blends on-time, reliability, and geofence signals. Volume metrics (incidents / MRs / tours) are surfaced separately.
            </p>
          </div>
          <SitePerformanceCard
            range={range}
            rangeLabel={rangeLabel}
            cadence={cadence}
            onCadenceChange={setCadence}
          />
        </section>

        <section aria-labelledby="dashboard-ops-heading" className="space-y-4">
          <div>
            <h2 id="dashboard-ops-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
              Operations Pulse
            </h2>
            <p className="text-zinc-500 text-sm mt-1">Volume trends and the freshest incident wire.</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 border border-[#27272A] bg-[#121212]">
              <div className="px-5 py-4 border-b border-[#27272A] flex items-center justify-between">
                <div>
                  <div className="label-mute">Operations Volume</div>
                  <div className="font-display uppercase font-bold text-xl mt-1">Shifts • Last 7 Days</div>
                </div>
                <PillBadge tone="blue">LIVE</PillBadge>
              </div>
              <div className="p-5 h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats?.shift_timeline || []}>
                    <CartesianGrid stroke={gridStroke} vertical={false} />
                    <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                    <YAxis stroke="#71717A" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={TOOLTIP_CURSOR} />
                    <Bar dataKey="shifts" fill="#FF3B30" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border border-[#27272A] bg-[#121212] flex flex-col">
              <div className="px-5 py-4 border-b border-[#27272A] flex items-center justify-between">
                <div>
                  <div className="label-mute">Latest Reports</div>
                  <div className="font-display uppercase font-bold text-xl mt-1">Recent Incidents</div>
                </div>
              </div>
              <div className="flex-1 divide-y divide-[#27272A]" data-testid="dashboard-recent-incidents">
                {(stats?.recent_incidents || []).slice(0, 5).map((i) => (
                  <div key={i.id} className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium truncate">{i.title}</div>
                      <PillBadge tone={severityTone(i.severity)}>{i.severity}</PillBadge>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 font-mono">
                      {fmtDateTime(i.created_at, i.site_timezone)}
                    </div>
                  </div>
                ))}
                {(!stats?.recent_incidents || stats.recent_incidents.length === 0) && (
                  <div className="p-8 text-center text-zinc-500 text-sm scanlines flex-1">
                    No incidents reported. All clear.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="dashboard-scan-photos-heading" className="space-y-4">
          <div>
            <h2 id="dashboard-scan-photos-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
              Scan Photo Snapshots
            </h2>
            <p className="text-zinc-500 text-sm mt-1">
              Freshest tour-scan captures from Detailed-DAR sites — a quick eyeball check that  are actually walking their routes.
            </p>
          </div>
          <RecentScanPhotos limit={12} />
        </section>

        <section aria-labelledby="dashboard-absence-heading" className="space-y-4">
          <div>
            <h2 id="dashboard-absence-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
              Absence Insights
            </h2>
            <p className="text-zinc-500 text-sm mt-1">Reason breakdown across the last 30 days.</p>
          </div>
          <AbsenceInsights />
        </section>
      </div>

      <DashboardCustomizeModal
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        role={role}
        visible={visible}
        sectionLabels={sectionLabels}
        onSave={persistPrefs}
      />
    </div>
  );
}
